import axios, { AxiosError } from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { formatRefId } from '../../utils/referenceHelpers';
import { CustomerData } from './CreditAccountService';
import { getOrCreateCreditAccountId, putWithCreditAccountRetry } from './putWithCreditAccountRetry';

export interface DepositInstructionPayload {
  amount: string;
  currency: string;
}

export interface DepositInstructionResponse {
  id: string;
  amount: string;
  currency: string;
  transactionRef: string;
  creditAccountRef: string;
}

export interface UpsertDepositInstructionParams {
  /** The credit account ID from R-Series (can be empty/0 if customer doesn't have one yet) */
  creditAccountId: string | number;
  /** The customer ID from R-Series (required if creditAccountId is empty/0) */
  customerId: string;
  /** The sale/transaction ID */
  saleId: string;
  /** The deposit amount */
  amount: number;
  /** The currency code (e.g., 'USD', 'CAD') */
  currency: string;
  /** Customer data - required if credit account may not exist on Invoicing */
  customer?: CustomerData;
}

export default class DepositInstructionService {
  /**
   * Creates or updates a deposit instruction for a transaction.
   * If the customer doesn't have a credit account on R-Series, it will create one with $0 limit.
   * If the credit account doesn't exist on Invoicing (404), it will create it and retry.
   *
   * @param params The parameters for upserting a deposit instruction
   * @returns The deposit instruction response with the credit account ID used
   */
  static async upsertDepositInstruction(
    params: UpsertDepositInstructionParams
  ): Promise<DepositInstructionResponse & { creditAccountId: string }> {
    const { creditAccountId, customerId, saleId, amount, currency, customer } = params;

    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    const creditAccountIdStr = await getOrCreateCreditAccountId(creditAccountId, customerId);
    const creditAccountRef = formatRefId(creditAccountIdStr);
    const transactionRef = formatRefId(`${saleId}`);

    // API expects amount as a string-encoded decimal
    const payload: DepositInstructionPayload = {
      amount: amount.toString(),
      currency,
    };

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/deposit-instruction`;

    return putWithCreditAccountRetry<DepositInstructionPayload, DepositInstructionResponse>({
      url,
      payload,
      creditAccountIdStr,
      creditAccountRef,
      transactionRef,
      currency,
      customer,
      operationName: 'deposit instruction',
      datadogContext: { amount },
    });
  }

  /**
   * Deletes a transaction and its associated deposit instruction from Invoicing.
   * This is idempotent - returns successfully even if the transaction doesn't exist.
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteDepositInstruction(
    creditAccountId: string | number,
    saleId: string
  ): Promise<void> {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      // No credit account means no transaction to delete on Invoicing
      return;
    }

    if (!saleId) {
      return;
    }
    const creditAccountRef = formatRefId(`${creditAccountId}`);
    const transactionRef = formatRefId(`${saleId}`);

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/deposit-instruction`;

    try {
      await axios.delete(url);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        return;
      }

      if (axiosError.response?.status === 409) {
        const data = axiosError.response.data as { message?: string } | undefined;
        const msg = (data?.message || '').toLowerCase();
        // Treat finalized-transaction conflicts as idempotent: if the transaction is already
        // completed and cannot be mutated, consider the delete successful for cleanup purposes.
        if (msg.includes('transaction') && msg.includes('already completed')) {
          return;
        }
      }
      datadogRum.addError(error, {
        creditAccountRef,
        transactionRef,
        message: `Failed to delete transaction for transactionRef: ${transactionRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
